import "vite/modulepreload-polyfill";
import Alpine from "alpinejs";
import "../css/main.css";
import "flatpickr/dist/flatpickr.css";
import * as Sentry from "@sentry/browser";

// Import Alpine.js plugins
import focus from "@alpinejs/focus";
// @ts-ignore - No type definitions available for @alpinejs/ui
import ui from "@alpinejs/ui";

import { FindingTable } from "../ts/findings/main";
import { ProjectTable, CodebaseTable } from "../ts/projects/main";
import { ScanTable } from "./scans/main";
import { PackageTable } from "./projects/packages/main";
import { FilterTable, IndividualFilter } from "./filters/main";
import { LanguageSelector } from "./languages/main";
import { ButtonsTab } from "./tab/main";

import { PlanForm } from "./accounts/main";

import { PackageRepository } from "./projects/packages/repository";
import {
  FindingsTableItem,
  LifeCycleStatusIndividual,
  PackageTableData,
  ProjectTableData,
  ScanTableData,
  ScoreInividual,
  UserTableData,
  InviteToSendData,
  VerificationEmailData,
  CodebaseTableData,
} from "../models/tables";
import { ScanRepository } from "./scans/repository";
import { FindingRepository } from "./findings/repository";

import { FilterData } from "../models/filters";
import { capitalize, displayDate, getBadgeColor, getLabel, getLevelColor, getStatusIcon, labelColor } from "./utils";
import { storeFilterData } from './constants';

import { ProjectRepository, CodebaseRepository } from "./projects/repository";
import { policiesTab, policyWizard, excludePatternsManager, ignoreSeveritiesManager, ignoredVulnerabilitiesFormsetManager, clonePolicyComponent } from "./policies/main";
import { fixVulnerabilityTab } from "./findings/main";
import { findingDetailView } from "./findings/detail";
import { Notifications } from "./toast/main";
import { Modal } from "./modals/main";

import onboarding from './onboarding/main';
import firewall_onboarding from './firewall_onboarding/main';
import { connectToChannel } from './websockets/ws';

// Import firewall component without unused interfaces
import { firewallComponent } from './firewall/main';

import FirewallIndicator from './firewall/components/FirewallIndicator';

// Add global declarations
declare global {
  interface Window {
    platformEnabled: boolean;
    Sentry: typeof Sentry;
    Alpine: typeof Alpine;
    organization?: {
      firewall_enabled: boolean;
    };
  }
}

if (import.meta.env.PROD) {
    Sentry.init({
      dsn: "https://70fdbbfba9c7f8530efcfa0113b06717@o136163.ingest.us.sentry.io/4508699562344448",
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0
    });

    // Add global error handlers
    window.addEventListener('error', (event) => {
      Sentry.captureException(event.error);
    });

    window.addEventListener('unhandledrejection', (event) => {
      Sentry.captureException(event.reason);
    });
} else {
    console.log("Development Mode: Sentry initialization skipped.");
}

console.log(`
  ███████╗ █████╗ ███████╗███████╗████████╗██╗   ██╗
  ██╔════╝██╔══██╗██╔════╝██╔════╝╚══██╔══╝╚██╗ ██╔╝
  ███████╗███████║█████╗  █████╗     ██║    ╚████╔╝
  ╚════██║██╔══██║██╔══╝  ██╔══╝     ██║     ╚██╔╝
  ███████║██║  ██║██║     ███████╗   ██║      ██║
  ╚══════╝╚═╝  ╚═╝╚═╝     ╚══════╝   ╚═╝      ╚═╝

  🚀 We're hiring! Join our team: https://jobs.ashbyhq.com/safety
`);

Alpine.plugin(focus);
Alpine.plugin(ui);

// Register Alpine.js global objects
window.Alpine = Alpine;

Alpine.store("theme", {
  darkMode: localStorage.getItem('theme') === 'dark',
  toggle() {
    this.darkMode = !this.darkMode;
  }
} as any);

Alpine.store("userMenuOptions", {
  items: [
    // { title: "Your Profile", url: "/profile" },
    { title: "Sign Out", url: "/logout" },
  ],
});

try {
  Alpine.store("mainMenuHeaderOptions", {
    items: [
        { title: window.platformEnabled ? "Codebases" : "Dashboard", url: "/" },
        {
          title: "Firewall",
          url: "/firewall",
          showIf: () => {
            try {
              return !!window.organization?.firewall_enabled;
            } catch (e) {
              return false;
            }
          }
        },
        {
          title: "AI Assistants",
          url: "/ai-assistants",
          platformEnabledOnly: true
        },
        { title: "Organization", url: "/organization" },
    ]
  });
} catch (e) {
  console.error("Error initializing mainMenuHeaderOptions:", e);
}

Alpine.store("organizationMenuOptions", {
  items: [
    { title: "Team", url: "/organization/team" },
    { title: "Policies", url: "/organization/policies", platformEnabledOnly: true },
    { title: "API Keys", url: "/organization/apikeys" },
    { title: "Settings", url: "/organization/settings" },
    { title: "Billing", url: "/organization/plan" },
    { title: "Usage", url: "/organization/usage" },
  ],
});

Alpine.store("lifeCycleStages", {
  items: [
    {
      name: "development", title: "Development", shortTitle: "Dev", cta: "Configure development scanning",
      description: "The project is in its development phase. At this stage, features are being built and the structure of the project is evolving. The system is not ready for live usage, and will likely have many changes, bugs, and enhancements to be addressed."
    },
    {
      name: "cicd", title: "CI/CD", shortTitle: "CI/CD", cta: "Configure ci/cd security",
      description: "Is a set of practices that aims to automate the software development and deployment processes."
    },
    {
      name: "production", title: "Production", shortTitle: "Prod", cta: "Configure production security",
      description: "The system is in a production environment. It's being used actively for its intended purpose and is maintained with regular updates and patches to ensure stability, performance, and security. At this stage, changes are usually limited to maintenance, minor updates, and responding to any new issues that arise."
    },
  ],
});

Alpine.store('findingsTableFilters', storeFilterData.findingsTableFilters);
Alpine.store('projectFindingsTableFilters', storeFilterData.projectFindingsTableFilters);
Alpine.store('scansTableFilters', storeFilterData.scansTableFilters);
Alpine.store('packagesTableFilters', storeFilterData.packagesTableFilters);
Alpine.store('codebaseScansTableFilters', storeFilterData.codebaseScansTableFilters);

interface ApiKeyStore {
  apiKey: string;
  setApiKey(key: string): void;
}

// Add type declaration for Alpine store
declare module 'alpinejs' {
  interface Alpine {
    store<T>(name: string): T;
  }
}

Alpine.store("apiKeyStore", {
  apiKey: '',
  setApiKey(key: string) {
    this.apiKey = key;
  }
} as ApiKeyStore);

interface ApiKeyResponse {
  status: string;
  api_key: string;
  uuid: string;
  user_email: string;
  created: string;
  message: string;
}

document.addEventListener("alpine:init", () => {
  Alpine.data("ProjectRepository", (..._initialStateArgs) => {
    const result = new ProjectRepository();
    return {
      ...result,
      getAll: result.getAll,
      branches: result.branches,
      confirmName: result.confirmName,
      delete: result.delete,
      reset: result.reset
    };
  });

  Alpine.data("CodebaseRepository", (..._initialStateArgs) => {
    const result = new CodebaseRepository();
    return {
      ...result,
      getAll: result.getAll,
      branches: result.branches,
      confirmName: result.confirmName,
      delete: result.delete,
      reset: result.reset
    };
  });

  Alpine.data("ProjectTable", (...initialStateArgs) => {
    const data = initialStateArgs[0] as ProjectTableData[];
    const result = new ProjectTable(data);
    return {
      ...result,
      sort: result.sort,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
      toggleAllChecked: result.toggleAllChecked,
      onChangeItemChecked: result.onChangeItemChecked,
      loadData: result.loadData,
    };
  });

  Alpine.data("CodebaseTable", (...initialStateArgs) => {
    const data = initialStateArgs[0] as CodebaseTableData[];
    const result = new CodebaseTable(data);
    return {
      ...result,
      sort: result.sort,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
      toggleAllChecked: result.toggleAllChecked,
      onChangeItemChecked: result.onChangeItemChecked,
      loadData: result.loadData,
    };
  });

  Alpine.data("FindingRepository", (..._initialStateArgs) => {
    const result = new FindingRepository();
    return {
      ...result,
      getAll: result.getAll,
      getByProject: result.getByProject,
      getByScan: result.getByScan,
      getMetaData: result.getMetaData,
      updateStatus: result.updateStatus,
      ignore: result.ignore,
      groupByPackage: result.groupByPackage.bind(result)
    };
  });
  Alpine.data("findingDetailView", findingDetailView);
  Alpine.data("FindingTable", (...initialStateArgs) => {
    const data = initialStateArgs[0] as FindingsTableItem[];
    const result = new FindingTable(data);
    return {
      ...result,
      sort: result.sort,
      filterTable: result.filterTable,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
      allTableData: result.allTableData,
      loadData: result.loadData,
      groupFindings: result.groupFindings
    };
  });
  Alpine.data("ScanRepository", (..._initialStateArgs) => {
    const result = new ScanRepository();
    return {
      ...result,
      getAll: result.getAll,
      getByProject: result.getByProject,
      getByFinding: result.getByFinding,
    };
  });
  Alpine.data('ScanTable', (...initialStateArgs) => {
    const data = initialStateArgs[0] as ScanTableData[];
    const result = new ScanTable(data);
    return {
      ...result,
      sort: result.sort,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      firstPage: result.firstPage,
      resetTableData: result.resetTableData,
      pagedTableData: result.pagedTableData,
      filterTable: result.filterTable,
      prepareDisplayedScans: result.prepareDisplayedScans,
      loadData: result.loadData,
      defaultSorting: result.defaultSorting,
      compareValues: result.compareValues,
      groupByProject: result.groupByProject,
      setIsProject: result.setIsProject,
    };
  });
  Alpine.data("PackageRepository", (..._initialStateArgs) => {
    const result = new PackageRepository();
    return {
      ...result,
      getByProject: result.getByProject,
    };
  });
  Alpine.data("PackageTable", (...initialStateArgs) => {
    const data = initialStateArgs[0] as PackageTableData[];
    const result = new PackageTable(data);
    return {
      ...result,
      sort: result.sort,
      filterTable: result.filterTable,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
      searchTable: result.searchTable,
      loadData: result.loadData,
      trimPaths: result.trimPaths,
      groupedByName: result.groupedByName,
      allGroupedPackages: result.allGroupedPackages,
      groupPackagesByName: result.groupPackagesByName,
      getVersionsText: result.getVersionsText
    };
  });
  Alpine.data("UserRepository", (..._initialStateArgs) => {
    const result = new UserRepository();
    return {
      ...result,
      getByProject: result.getByProject,
      getByOrganization: result.getByOrganization,
      addMember: result.addMember,
      activateMember: result.activateMember,
      removeMember: result.removeMember,
      changeRole: result.changeRole
    };
  });
  Alpine.data("InviteRepository", (...initialStateArgs) => {
    const organization_id = initialStateArgs[0] as number
    const result = new InviteRepository(organization_id);
    return {
      ...result,
      getByOrganization: result.getByOrganization,
      createInvites: result.createInvites,
      cancelInvite: result.cancelInvite,
      addInvite: result.addInvite,
      resendInvite: result.resendInvite
    };
  });
  Alpine.data("UserTable", (...initialStateArgs) => {
    const data = initialStateArgs[0] as UserTableData[];
    const result = new UserTable(data);
    return {
      ...result,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
      searchTable: result.searchTable,
      loadData: result.loadData
    };
  });
  Alpine.data("InviteToSendTable", (...initialStateArgs) => {
    let data = initialStateArgs[0] as InviteToSendData[];
    let result = new InviteToSendTable(data);

    Alpine.effect(() => {
      data = initialStateArgs[0] as InviteToSendData[];
      result = new InviteToSendTable(data);
    });

    return {
      ...result,
      nextPage: result.nextPage,
      previousPage: result.previousPage,
      pagedTableData: result.pagedTableData,
    };
  });
  Alpine.data("RiskScoreInputRepository", (..._initialStateArgs) => {
    const result = new RiskScoreInputRepository();
    return {
      ...result,
      getByProject: result.getByProject,
      createInput: result.createInput,
      updateLevel: result.updateLevel,
    };
  });
  Alpine.data("RiskScoreInputLevelRepository", (..._initialStateArgs) => {
    const result = new RiskScoreInputLevelRepository();
    return {
      ...result,
      getByType: result.getByType
    };
  });
  Alpine.data("TableFilter", (...initialStateArgs) => {
    const filterData = initialStateArgs[1] as FilterData;
    const result = new FilterTable(filterData);
    return {
      ...result,
      setFilterData: result.setFilterData,
      selectOnlyOneOption: result.selectOnlyOneOption,
      clearFilters: result.clearFilters,
    };
  });
  Alpine.data("IndividualFilter", (..._initialStateArgs) => {
    const result = new IndividualFilter();
    return {
      ...result,
      toggle: result.toggle,
      close: result.close,
      handleAddFilter: result.handleAddFilter,
      rangePicker: result.rangePicker,
    };
  });

  Alpine.data("LanguageSelector", (...initialStateArgs: unknown[]) => {
    const defaultOnly: boolean = initialStateArgs[0] as boolean;
    const currentLangCode: string = initialStateArgs[1] as string;

    const result = new LanguageSelector(defaultOnly, currentLangCode);
    return {
      ...result,
      toggle: result.toggle,
      close: result.close,
      selectLanguage: result.selectLanguage
    }
  });

  Alpine.data("ButtonsTab", (..._initialStateArgs) => {
    const result = new ButtonsTab();
    return {
      ...result,
      setCurrentTab: result.setCurrentTab
    }
  });

  Alpine.data("Notifications", (..._initialStateArgs) => {
    const result = new Notifications();

    return {
      ...result,
      add: result.add,
      remove: result.remove
    }
  });

  Alpine.data("Modals", (..._initialStateArgs) => {
    const result = new Modal();

    return {
      ...result,
      openModal: result.openModal,
      closeModal: result.closeModal,
    }
  });


  Alpine.data("EmailVerificationAlert", (...initialStateArgs) => {
    /*
    * The EmailVerificationAlert data currently handles email verification alerts.
    * It follows a similar data structure to Django's messages framework,
    * which makes it well-suited for handling system-wide alerts.
    * In the future, this class could be extended to handle more types of system alerts.
    */

    let { userEmailVerified, userEmail, csrfToken, emailVerificationResent } = initialStateArgs[0] as VerificationEmailData;

    let alerts = [];

    if (!userEmailVerified && !emailVerificationResent) {
      const verificationMessage = `
          Your email (<strong>${userEmail}</strong>) is unverified. `;

      alerts.push({
        tags: "warning",
        message: verificationMessage,
        extra_tags: "safe",
      });
    }

    const displayMessage = (alert: {
      tags: string;
      message: string;
      extra_tags: string;
    }) => {
      // Instead of declaring a separate function that's flagged as unused,
      // we'll inline the HTML escaping logic only when needed
      return alert.extra_tags.includes('safe')
        ? alert.message
        : (() => {
            const text = document.createTextNode(alert.message);
            const div = document.createElement('div');
            div.appendChild(text);
            return div.innerHTML;
          })();
    };

    const sendVerificationEmail = async (): Promise<void> => {
      await fetch('/account/resend-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken!
        },
        body: JSON.stringify({})
      });
      window.location.reload();
    }

    return {
      alerts,
      displayMessage,
      sendVerificationEmail,
    }
  });

  // @ts-ignore
  Alpine.data("labelColor", (...initialStateArgs) => {
    const label = initialStateArgs[0] as string;
    return labelColor(label)
  });

  // @ts-ignore
  Alpine.data("getLevelColor", (...initialStateArgs) => {
    const label = initialStateArgs[0] as ScoreInividual;
    return getLevelColor(label)
  });

  // @ts-ignore
  Alpine.data("getBadgeColor", (...initialStateArgs) => {
    const status = initialStateArgs[0] as LifeCycleStatusIndividual;
    return getBadgeColor(status)
  });

  // @ts-ignore
  Alpine.data("getStatusIcon", (...initialStateArgs) => {
    const status = initialStateArgs[0] as LifeCycleStatusIndividual;
    return getStatusIcon(status)
  });

  // @ts-ignore
  Alpine.data("getLabel", (...initialStateArgs) => {
    const score = initialStateArgs[0] as number;
    return getLabel(score)
  });

  // @ts-ignore
  Alpine.data("displayDate", (...initialStateArgs) => {
    const date = initialStateArgs[0] as number;
    return displayDate(date)
  });

  // @ts-ignore
  Alpine.data("capitalize", (...initialStateArgs) => {
    const string_ = initialStateArgs[0] as string;
    return capitalize(string_)
  });

  Alpine.data("policiesTab", policiesTab);
  Alpine.data("ignoreSeveritiesManager", ignoreSeveritiesManager);
  Alpine.data("ignoredVulnerabilitiesFormsetManager", ignoredVulnerabilitiesFormsetManager);
  Alpine.data("WizardForm", (...initialStateArgs) => {
    const currentStep = initialStateArgs[0] as string;
    return policyWizard(currentStep);
  });
  Alpine.data("excludePatternsManager", (...initialStateArgs) => {
    const excludePatternsFieldId = initialStateArgs[0] as string;
    const defaultMaxDepth = typeof initialStateArgs[1] === 'number' ? initialStateArgs[1] : 4;
    return excludePatternsManager(excludePatternsFieldId, defaultMaxDepth);
  });

  Alpine.data("fixVulnerabilityTab", fixVulnerabilityTab);

  Alpine.data("Tooltip", (..._initialStateArgs) => {
    return {
      init() {
        this.initializeTooltip(this.$el, this.$el.dataset.tooltip || "", {
          theme: this.$el.dataset.tooltipTheme || "light",
          placement: this.$el.dataset.tooltipPlacement || "top",
        });
      },
      initializeTooltip(el: HTMLElement, content: string, options: any) {
        if (!content) return;

        // Set title attribute for native tooltip
        el.setAttribute("title", content);

        // Add tooltip classes for styling
        el.classList.add("native-tooltip");
        if (options.theme) {
          el.classList.add(`tooltip-theme-${options.theme}`);
        }
        if (options.placement) {
          el.classList.add(`tooltip-placement-${options.placement}`);
        }
      }
    };
  });
  Alpine.data("PlanForm", (...initialStateArgs) => {
    return { planForm: new PlanForm(...(initialStateArgs as [PlanType, BillingFrequency, number, number, PlanType?]))};
  });
  Alpine.data("betaAccessBlock", (..._initialStateArgs) => {
    return {
      showCTA: false,
      checkCTA() {
        const cookieItems = document.cookie ? document.cookie.split('; ') : [];
        const found = cookieItems.find(row => typeof row === 'string' && row.startsWith('beta_cta_hidden='));
        this.showCTA = !found;
      },
      hideCTA() {
          document.cookie = "beta_cta_hidden=true; path=/; max-age=" + (60 * 60 * 24 * 30); // 30 days
          this.showCTA = false;
      }
  }
  })

  Alpine.data("apiKeysManager", (...initialStateArgs: unknown[]) => {
    const initialKeys = initialStateArgs[0] as Array<{
      uuid: string;
      key: string;
      user: {
        email: string;
      };
      created: string;
    }> || [];
    return {
      userKeys: initialKeys.map(key => ({
        uuid: key.uuid,
        key: key.key,
        user: {
          email: key.user.email
        },
        created: key.created
      })),
      addNewKey(keyData: { uuid: string; key: string; user: { email: string }; created: string }) {
        this.userKeys = [...this.userKeys, keyData];
      },
      formatDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    };
  });

  Alpine.data("apiKeyCreator", (initialKey = '') => ({
    generating: false,
    get apiKey() {
      return (Alpine.store<ApiKeyStore>('apiKeyStore')).apiKey || initialKey;
    },
    createKey() {
      if (this.generating) return;
      this.generating = true;
      fetch('/organization/apikeys/create', {
        method: 'POST',
        headers: {
          'X-CSRFToken': (document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement).value,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
        .then(response => {
          if (!response.ok) {
            // Handle different error status codes
            if (response.status === 429) {
              throw new Error('Rate limit exceeded. Please try again later.');
            } else if (response.status === 401) {
              throw new Error('You must be logged in to create an API key.');
            } else if (response.status === 400) {
              throw new Error('You must be part of an organization to create an API key.');
            } else {
              throw new Error('Failed to create API key. Please try again.');
            }
          }
          return response.json() as Promise<ApiKeyResponse>;
        })
        .then(data => {
          if (data.status === "success") {
            (Alpine.store<ApiKeyStore>('apiKeyStore')).setApiKey(data.api_key);
            // Dispatch event with the new key data
            this.$dispatch('key-created', {
              uuid: data.uuid,
              key: data.api_key,
              user: {
                email: data.user_email
              },
              created: data.created
            });
            this.$dispatch('notify', {
              content: data.message,
              type: 'success',
              displayDuration: 5000
            });
          } else {
            this.$dispatch('notify', {
              content: data.message || 'Failed to create API key.',
              type: 'error',
              displayDuration: 5000
            });
          }
        })
        .catch(err => {
          console.error('API Key creation error:', err);
          this.$dispatch('notify', {
            content: err.message || 'Error creating API key. Please try again.',
            type: 'error',
            displayDuration: 5000
          });
        })
        .finally(() => {
          this.generating = false;
        });
    }
  }));


  Alpine.data("apiKeyGenerator", (...initialStateArgs: unknown[]) => {
    const initialKey = (initialStateArgs[0] as string) || '';
    return {
      key: initialKey,
      copied: false,
      revealed: false,
      async onClick() {
        // Always reveal on click
        this.revealed = true;

        try {
          // Try to copy to clipboard
          await navigator.clipboard.writeText(this.key);
          this.copied = true;
          setTimeout(() => { this.copied = false; }, 2000);
        } catch (err) {
          // If clipboard operation fails, show error state briefly
          console.error('Failed to copy to clipboard:', err);
          this.copied = false;
        }
      }
    };
  });

  // Add Firewall component using the imported implementation
  Alpine.data("Firewall", firewallComponent);

  Alpine.data("onboarding", onboarding);

  Alpine.data("firewall_onboarding", firewall_onboarding);

  // Register the FirewallIndicator component
  Alpine.data("FirewallIndicator", FirewallIndicator);

  // Register the TableTooltip component
  Alpine.data("TableTooltip", TableTooltip);

  Alpine.data("clonePolicyComponent", clonePolicyComponent);
});

Alpine.start();

// Test here
import { loadTheme, setupTheme } from "./theme";
import { InviteRepository, UserRepository } from "./users/repository";
import { InviteToSendTable, UserTable } from "./users/main";
import { RiskScoreInputLevelRepository, RiskScoreInputRepository } from "./projects/risk_score_inputs/repository";
import { BillingFrequency, PlanType } from "../models/components";
import { TableTooltip } from "./findings/main";

window.addEventListener("DOMContentLoaded", () => {
  loadTheme();
  connectToChannel();
});
setupTheme(document.querySelector<HTMLButtonElement>("#theme-toggle")!);

// Make native tooltips available globally
window.Alpine = Alpine;
